var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { json } from "@hyper-hyper-space/hhs3_json";
import { HASH_SHA256 } from "@hyper-hyper-space/hhs3_crypto";
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { formatValidationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { RootScopedDag } from "@hyper-hyper-space/hhs3_mvt";
import { createSyncSession } from "@hyper-hyper-space/hhs3_sync";
import { deserializePublicKeyFromBase64, signPayload as signPayloadHelper } from "../../authorship.js";
import { RCAP_TYPE_ID, MAX_CAP_ORIGINS, } from "./payload.js";
import { validateRCapPayload } from "./validate.js";
import { RCapViewImpl } from "./view.js";
import { RCapDeltaAccumulator, computeRCapDelta } from "./delta.js";
const DEFAULT_ENROLL_CAPABILITY = 'enroll';
export const rCapFactory = {
    computeRootObjectId: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const entry = dag.createEntry(payload, {}, position(), ctx.getCrypto().hash(HASH_SHA256));
        return entry.hash;
    }),
    validateCreationPayload: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () { return validateRCapPayload(payload, { mode: "create", ctx }); }),
    executeCreationPayload: (payload, _ctx, scopedDag) => __awaiter(void 0, void 0, void 0, function* () {
        const createPayload = payload;
        const meta = {
            caps: json.toSet(Object.keys(createPayload.initialCaps)),
        };
        return yield scopedDag.append(payload, meta, position());
    }),
    loadObject: (id, ctx, opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
        const rawDag = yield ctx.getDag(id, backendLabel);
        if (rawDag === undefined)
            throw new Error(`DAG '${id}' not found`);
        const scopedDag = new RootScopedDag(rawDag);
        const createOp = (yield scopedDag.loadEntry(id)).payload;
        return new RCapImpl(id, createOp, ctx, backendLabel);
    })
};
export class RCapImpl {
    constructor(createOpId, createOp, ctx, backendLabel = 'default') {
        this.meshConfig = { meshLabel: 'default' };
        this.deltaStrategy = 'bounded';
        this.createOpId = createOpId;
        this.createOp = createOp;
        this.ctx = ctx;
        this.backendLabel = backendLabel;
    }
    getBackendLabel() {
        return this.backendLabel;
    }
    getId() { return this.createOpId; }
    getType() { return _a.typeId; }
    getInitialCaps() { return this.createOp.initialCaps; }
    getEnrollCapabilityName() {
        var _b;
        return (_b = this.createOp.enrollCapability) !== null && _b !== void 0 ? _b : DEFAULT_ENROLL_CAPABILITY;
    }
    isCreator(keyId) {
        return this.createOp.creators.includes(keyId);
    }
    lookupCreatorKey(keyId) {
        const idx = this.createOp.creators.indexOf(keyId);
        if (idx < 0)
            return undefined;
        return deserializePublicKeyFromBase64(this.createOp.creatorKeys[idx]);
    }
    lookupKey(keyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const creatorKey = this.lookupCreatorKey(keyId);
            if (creatorKey !== undefined)
                return creatorKey;
            const scopedDag = yield this.getScopedDag();
            const frontier = yield scopedDag.getFrontier();
            const cover = yield scopedDag.findCoverWithFilter(frontier, { containsValues: { ids: [keyId] } });
            for (const hash of cover) {
                const entry = yield scopedDag.loadEntry(hash);
                if (entry === undefined)
                    continue;
                const p = entry.payload;
                if (p.action === 'add-identity' && p.keyId === keyId) {
                    return deserializePublicKeyFromBase64(p.publicKey);
                }
            }
            return undefined;
        });
    }
    getHashSuite() {
        return this.ctx.getHashSuite();
    }
    selfValidate() {
        return this.ctx.getConfig().selfValidate || false;
    }
    applyValidatedPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.selfValidate()) {
                const result = yield this.validatePayload(payload, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return this.applyPayload(payload, at);
        });
    }
    // Convenience methods
    addIdentity(keyId, publicKey, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                action: 'add-identity', keyId, publicKey,
            };
            const signed = yield signPayloadHelper(payload, author);
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            return this.applyValidatedPayload(signed, at);
        });
    }
    createCap(capName, managedBy, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                action: 'create-cap', capName, managedBy,
            };
            const signed = yield signPayloadHelper(payload, author);
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            return this.applyValidatedPayload(signed, at);
        });
    }
    deleteCap(capName, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                action: 'delete-cap', capName,
            };
            const signed = yield signPayloadHelper(payload, author);
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            return this.applyValidatedPayload(signed, at);
        });
    }
    grant(grantee, capName, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at || (yield scopedDag.getFrontier());
            const view = yield this.getView(at, at);
            const capOrigins = Array.from(yield view.currentCapCreationVersion(capName))
                .sort()
                .slice(0, MAX_CAP_ORIGINS);
            const payload = {
                action: 'grant', grantee, capName, capOrigins,
            };
            const signed = yield signPayloadHelper(payload, author);
            return this.applyValidatedPayload(signed, at);
        });
    }
    revoke(grantee, capName, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                action: 'revoke', grantee, capName,
            };
            const signed = yield signPayloadHelper(payload, author);
            const dag = yield this.getScopedDag();
            at = at || (yield dag.getFrontier());
            return this.applyValidatedPayload(signed, at);
        });
    }
    // RObject interface
    validatePayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return validateRCapPayload(payload, { mode: "op", cap: this, at });
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const p = payload;
            const meta = {};
            const scopedDag = yield this.getScopedDag();
            switch (p.action) {
                case 'add-identity':
                    meta['ids'] = json.toSet([p.keyId]);
                    break;
                case 'create-cap':
                    meta['caps'] = json.toSet([p.capName]);
                    break;
                case 'delete-cap':
                    meta['caps'] = json.toSet([p.capName]);
                    meta['barrier'] = json.toSet(['t']);
                    break;
                case 'grant':
                    meta['grants'] = json.toSet([p.capName + ':' + p.grantee]);
                    break;
                case 'revoke':
                    meta['grants'] = json.toSet([p.capName + ':' + p.grantee]);
                    meta['barrier'] = json.toSet(['t']);
                    break;
                default:
                    throw new Error("Invalid cap action in payload: " + p.action);
            }
            return yield scopedDag.append(payload, meta, at);
        });
    }
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at || (yield scopedDag.getFrontier());
            from = from || (yield scopedDag.getFrontier());
            return new RCapViewImpl(this, at, from);
        });
    }
    extractForeignDeps(_payload, _at) {
        return undefined;
    }
    subscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    setDeltaStrategy(strategy) {
        this.deltaStrategy = strategy;
    }
    computeDelta(start, end) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
            if (rawDag === undefined)
                throw new Error("DAG not found");
            return computeRCapDelta(this, rawDag, this.deltaStrategy, start, end);
        });
    }
    createDeltaAccumulator(start, end) {
        return new RCapDeltaAccumulator(this, start, end);
    }
    configure(config) {
        this.meshConfig = Object.assign(Object.assign({}, this.meshConfig), config);
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._scopedDag = new RootScopedDag(rawDag);
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._causalDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._causalDag = rawDag;
            }
            return this._causalDag;
        });
    }
    // SyncableObject interface
    startSync() {
        return __awaiter(this, void 0, void 0, function* () {
            var _b;
            if (this._syncSession !== undefined)
                return;
            const mesh = this.ctx.getMesh((_b = this.meshConfig.meshLabel) !== null && _b !== void 0 ? _b : 'default');
            this._swarm = mesh.createSwarm(this.createOpId);
            const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
            if (rawDag === undefined)
                throw new Error(`DAG '${this.createOpId}' not found`);
            const target = {
                dagId: this.createOpId,
                dag: rawDag,
                rObject: this,
                hashSuite: this.ctx.getHashSuite(),
            };
            this._syncSession = createSyncSession(target, [this._swarm]);
            this._swarm.activate();
        });
    }
    stopSync() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._syncSession !== undefined) {
                this._syncSession.destroy();
                this._syncSession = undefined;
            }
            if (this._swarm !== undefined) {
                this._swarm.destroy();
                this._swarm = undefined;
            }
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.stopSync();
            this._scopedDag = undefined;
        });
    }
}
_a = RCapImpl;
RCapImpl.create = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const { serializePublicKeyToBase64 } = yield import("../../authorship.js");
    const createPayload = {
        action: 'create',
        type: RCAP_TYPE_ID,
        seed: options.seed,
        creators: options.creators.map(c => c.keyId),
        creatorKeys: options.creators.map(c => serializePublicKeyToBase64(c.publicKey)),
        initialCaps: options.initialCaps,
        hashAlgorithm: options.hashAlgorithm || 'sha256',
    };
    if (options.enrollCapability !== undefined) {
        createPayload.enrollCapability = options.enrollCapability;
    }
    return createPayload;
});
RCapImpl.typeId = RCAP_TYPE_ID;
export { RCapViewImpl } from "./view.js";
export * from "./delta.js";
export const RCap = RCapImpl;
export const RCapView = RCapViewImpl;
